import { useTranslation } from "@/hooks";
import { useAiNotificationStore } from "@/stores/ai-notification";
import type { AiMessage } from "@/types/ai";
import { logger } from "@/utils/debug";
import {
  ActionIcon,
  Box,
  Flex,
  Input,
  Paper,
  Text,
  Tooltip,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconMinus, IconPlus } from "@tabler/icons-react";
import { useEffect, useRef, useState } from "react";
import { AiAvatar } from "../avatar/AiAvatar";
import "./AiChat.css";

export function AiChat({
  onPrompt,
  onFocus,
  onBlur,
  placeholder,
  width = 450,
  collapsed,
  defaultPrompt,
  autoFocus = true,
}: {
  onPrompt: (prompt: string) => Promise<void>;
  onFocus?: () => void;
  onBlur?: () => void;
  placeholder?: string;
  width?: number | string;
  collapsed?: boolean;
  defaultPrompt?: string;
  autoFocus?: boolean;
}) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(collapsed ?? false);
  const [messages, setMessages] = useState<AiMessage[]>([]);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const currentNotification = useAiNotificationStore(
    (state) => state.currentNotification
  );
  const lastNotificationIdRef = useRef<string | null>(null);
  const [newMessageIds, setNewMessageIds] = useState<Set<string>>(new Set());
  const prevMessagesLengthRef = useRef<number>(0);
  const prevLoadingRef = useRef<boolean>(false);
  const chatWindowRef = useRef<HTMLDivElement>(null);
  const clickedInsideRef = useRef<boolean>(false);

  const form = useForm({
    initialValues: {
      prompt: defaultPrompt || "",
    },
  });

  useEffect(() => {
    if (collapsed !== undefined) {
      setIsCollapsed(collapsed);
    }
  }, [collapsed]);

  useEffect(() => {
    if (messagesContainerRef.current && !isCollapsed) {
      setTimeout(() => {
        requestAnimationFrame(() => {
          if (messagesContainerRef.current) {
            messagesContainerRef.current.scrollTop =
              messagesContainerRef.current.scrollHeight;
          }
        });
      }, 50);
    }
  }, [messages, isCollapsed, loading]);

  useEffect(() => {
    if (currentNotification?.message) {
      const notificationId = `${currentNotification.message}-${currentNotification.type}-${currentNotification.timestamp}`;
      if (lastNotificationIdRef.current !== notificationId) {
        lastNotificationIdRef.current = notificationId;
        const messageId = Date.now().toString();
        const aiMessage: AiMessage = {
          id: messageId,
          role: "ai",
          content: currentNotification.message,
          timestamp: new Date(),
        };
        setNewMessageIds((prev) => new Set(prev).add(messageId));
        setMessages((prev) => [...prev, aiMessage]);
        setTimeout(() => {
          setNewMessageIds((prev) => {
            const next = new Set(prev);
            next.delete(messageId);
            return next;
          });
        }, 400);
      }
    }
  }, [currentNotification]);

  useEffect(() => {
    if (messages.length > prevMessagesLengthRef.current) {
      const newMessages = messages.slice(prevMessagesLengthRef.current);
      newMessages.forEach((msg) => {
        setNewMessageIds((prev) => new Set(prev).add(msg.id));
      });

      const delay = prevLoadingRef.current && !loading ? 100 : 0;
      setTimeout(() => {
        newMessages.forEach((msg) => {
          setNewMessageIds((prev) => {
            const next = new Set(prev);
            next.delete(msg.id);
            return next;
          });
        });
      }, delay + 400);
    }
    prevMessagesLengthRef.current = messages.length;
    prevLoadingRef.current = loading;
  }, [messages, loading]);

  function handleAvatarClick() {
    const newCollapsed = !isCollapsed;
    setIsCollapsed(newCollapsed);
    if (newCollapsed) {
      onBlur?.();
    } else {
      onFocus?.();
    }
  }

  async function handleSubmit(values: { prompt: string }) {
    const userMessage: AiMessage = {
      id: Date.now().toString(),
      role: "user",
      content: values.prompt,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setLoading(true);
    form.setValues({ prompt: "" });

    try {
      await onPrompt(values.prompt);
    } catch (err) {
      logger.error(err);
      setMessages((prev) => [
        ...prev,
        {
          id: (Date.now() + 1).toString(),
          role: "ai",
          content: t("aiChat").errorOccurred,
          timestamp: new Date(),
        },
      ]);
    } finally {
      setLoading(false);
    }
  }

  function handleMouseDown(e: React.MouseEvent<HTMLDivElement>) {
    if (chatWindowRef.current?.contains(e.target as Node)) {
      clickedInsideRef.current = true;
    }
  }

  function handleInputBlur(e: React.FocusEvent<HTMLInputElement>) {
    const currentTarget = e.currentTarget;
    const relatedTarget = e.relatedTarget as HTMLElement;
    const wasClickedInside = clickedInsideRef.current;
    clickedInsideRef.current = false;

    setTimeout(() => {
      if (!currentTarget) return;

      const chatWindow = currentTarget.closest(".ai-chat-window");
      const activeElement = document.activeElement as HTMLElement;

      if (
        wasClickedInside ||
        chatWindow?.contains(relatedTarget) ||
        chatWindow?.contains(activeElement)
      ) {
        return;
      }

      setIsCollapsed(true);
      onBlur?.();
    }, 100);
  }

  const chatWindowClassName = `ai-chat-window ${
    isCollapsed ? "ai-chat-window-collapsed" : "ai-chat-window-expanded"
  }`;

  const inputStyles = {
    input: {
      padding: "var(--mantine-spacing-xs)",
      color: loading
        ? "var(--mantine-color-gray-5)"
        : "var(--mantine-color-dark-9)",
    },
  };

  function getMessageClassName(message: AiMessage) {
    return `ai-chat-message ai-chat-message-${message.role}${
      newMessageIds.has(message.id) ? " ai-chat-message-new" : ""
    }`;
  }

  return (
    <div className="ai-chat-container">
      <Tooltip
        disabled={!isCollapsed}
        label={t("aiChat").askMeAnything}
        position="top"
        withArrow
        offset={4}
        openDelay={300}
        color="#011842"
        transitionProps={{ transition: "pop", duration: 200 }}
      >
        <ActionIcon
          onClick={handleAvatarClick}
          bdrs={60}
          size={60}
          className="ai-chat-avatar-button"
        >
          <AiAvatar size={60} />
        </ActionIcon>
      </Tooltip>
      <Paper
        ref={chatWindowRef}
        className={chatWindowClassName}
        style={{ width }}
        shadow="xl"
        p={0}
        onMouseDown={handleMouseDown}
      >
        <Flex
          p="xs"
          justify="space-between"
          align="center"
          className="ai-chat-header"
        >
          <Text fw={500} size="sm" c="dark.9">
            {t("aiChat").assistant}
          </Text>
          <ActionIcon
            variant="subtle"
            size="sm"
            onClick={handleAvatarClick}
            color="gray"
          >
            <IconMinus color="var(--mantine-color-gray-5)" size={16} />
          </ActionIcon>
        </Flex>

        <div ref={messagesContainerRef} className="ai-chat-messages">
          <Box px="md" pb={4} className="ai-chat-messages-content">
            {messages.map((message) => (
              <div key={message.id} className={getMessageClassName(message)}>
                <div className="ai-chat-message-bubble">
                  <Text
                    fz={14}
                    c={message.role === "user" ? "white" : "dark.9"}
                  >
                    {message.content}
                  </Text>
                </div>
              </div>
            ))}
            {loading && (
              <div className="ai-chat-message ai-chat-message-ai ai-chat-thinking-container">
                <div className="ai-chat-message-bubble ai-chat-thinking">
                  <Flex gap={3} align="center">
                    <div className="ai-chat-thinking-dot" />
                    <div className="ai-chat-thinking-dot" />
                    <div className="ai-chat-thinking-dot" />
                  </Flex>
                </div>
              </div>
            )}
          </Box>
        </div>

        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Flex gap="xs" p="xs" align="center">
            {!isCollapsed && (
              <Input
                readOnly={loading}
                placeholder={placeholder || t("aiChat").placeholder}
                variant="unstyled"
                className="ai-chat-input"
                autoFocus={autoFocus}
                flex={1}
                h={36}
                {...form.getInputProps("prompt")}
                onBlur={handleInputBlur}
                styles={inputStyles}
              />
            )}
            <Tooltip
              openDelay={500}
              label={t("aiChat").uploadFile}
              withArrow
              offset={10}
            >
              <ActionIcon
                c="gray.9"
                variant="subtle"
                bdrs="xl"
                size="xl"
                type="button"
              >
                <IconPlus
                  size={22}
                  stroke={1.6}
                  color="var(--mantine-color-gray-9)"
                />
              </ActionIcon>
            </Tooltip>
          </Flex>
        </form>
      </Paper>
    </div>
  );
}
